import React from 'react';
import Navigation from '@/components/Navigation';
import AnimatedBackground from '@/components/AnimatedBackground';
import { motion } from 'framer-motion';
import { 
  Zap, Code2, Cloud, Shield, Database, Wrench
} from 'lucide-react';

const currentLearning = [
  'Rust fundamentals',
  'Pulumi',
  'DuckDB',
];

const categories = [
  { id: 'cloud', name: 'Cloud & Infrastructure', icon: Cloud },
  { id: 'backend', name: 'Backend & Languages', icon: Code2 },
  { id: 'data', name: 'Data & Storage', icon: Database },
  { id: 'devops', name: 'DevOps & Tools', icon: Wrench },
  { id: 'security', name: 'Security & Compliance', icon: Shield }
];

const skills = {
  cloud: [
    'AWS', 'Microsoft Azure', 'Google Cloud Platform',
  ],
  backend: [
 'Python', 'TypeScript', 'NodeJS', 'BunJS', 'DenoJS', 'FastAPI', 'GraphQL', 'REST APIs', 'Microservices', 'Concurrency', 'API Design', 'API Security', 
  ],
  data: [
    'Graph Databases', 'Neo4j', 'AWS Neptune', 'Graph Modeling', 'Query Optimization',
    'Relational Databases', 'PostgreSQL', 'MySQL', 'Oracle', 'Performance Tuning',
    'NoSQL', 'DynamoDB', 'DocumentDB', 'Redis', 'Memcached', 'ElastiCache' , 'DuckDB', 'SQL'
  ],
  devops: [
    'Orchestration', 'Netflix Conductor', 'Azure Step Functions', 'Workflow Design', 'Process Automation',
    'CI/CD', 'GitHub Actions', 'Jenkins', 'Pipeline Design', 'Automation',
    'Infrastructure', 'Terraform', 'Docker', 'Pulumi'
  ],
  security: [
    'Cloud Security', 'IAM & Access Control','Compliance', 'Threat Detection',
    'Data Security', 'Encryption', 'Data Protection', 'Security Policies', 'Audit & Monitoring',
    'Identity Security', 'NHI Security', 'Identity Management', 'Access Governance', 'Authentication & Authorization',
    'Vulnerability Management', 'Security Scanning', ' Zero Trust', 
  ],
};

const Skills: React.FC = () => {
  return (
    <div className="min-h-screen relative">
      <AnimatedBackground />
      <Navigation />
      <main className="pt-16 pb-8 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          {/* Current Learning (compact pill bar) */}
          <div className="mb-4 flex justify-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/70 dark:bg-gray-800/60 border border-gray-200/60 dark:border-gray-700/60 px-3 py-1.5 shadow-sm">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-gray-800 dark:text-gray-100">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse"></span>
                Currently learning
              </span>
              <span className="h-3 w-px bg-gray-300 dark:bg-gray-700" />
              {currentLearning.slice(0, 3).map((item) => (
                <span
                  key={item}
                  className="inline-flex items-center gap-1.5 rounded-full border border-gray-200/60 dark:border-gray-700/60 px-2 py-0.5 text-[11px] text-gray-700 dark:text-gray-300 hover:border-green-500/50 transition-colors"
                >
                  <span className="inline-block h-1 w-1 rounded-full bg-green-500/80"></span>
                  <span className="font-mono">{item}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative mb-6"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-blue-500/10 rounded-xl transform -rotate-1"></div>
            <div className="relative bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50 dark:border-gray-700/50">
              <div className="flex items-center justify-center gap-3 mb-2">
                <motion.div 
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                  className="p-2 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg"
                >
                  <Zap className="w-5 h-5 text-white" />
                </motion.div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                  Technical Skills
                </h1>
              </div>
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-xs text-gray-600 dark:text-gray-300 text-center"
              >
                Core technical skills by domain
              </motion.p>
            </div>
          </motion.div>

          {/* Horizontal Skill Lists (now wrapped, no scroll) */}
          <div className="space-y-3">
            {categories.map((cat, index) => (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="relative group bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-200 dark:border-gray-700 hover:border-green-500/50 dark:hover:border-green-500/50 transition-all duration-300 hover:shadow-lg px-4 py-4 flex items-start gap-4"
              >
                <div className="flex-shrink-0 flex flex-col items-center min-w-[100px]">
                  <div className="p-2 mb-1.5 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg shadow-sm group-hover:shadow-md transition-shadow duration-300">
                    <cat.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xs font-semibold text-green-700 dark:text-green-300 text-center leading-tight">
                    {cat.name}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap gap-2 items-center justify-start">
                    {skills[cat.id].map((skill) => (
                      <span
                        key={skill}
                        className="px-2.5 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded-full whitespace-nowrap hover:bg-green-200 dark:hover:bg-green-900/50 hover:scale-105 transition-all duration-200 cursor-default"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Skills; 